import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../supabaseClient';
import SiteNavbar from '../components/SiteNavbar';
import SiteFooter from '../components/SiteFooter';
import LineIcon from '../components/LineIcon';
import Reveal, { CountUp } from '../components/Reveal';

const NAVY     = '#0D1B2A';
const AMBER    = '#F5A623';
const WHITE    = '#FFFFFF';
const GRAY_BG  = '#F6F6F6';
const FONT_HEAD = "'Bebas Neue', sans-serif";
const FONT_BODY = "'DM Sans', sans-serif";

// name is a marketing.tradesPlural key, resolved via t() at render time (see marketing.tradesPlural
// in en.json/es.json) -- keeps the stat count paired with a translated label without hardcoding
// English text into this module-level constant.
const SHORTAGE_TRADES = [
  { key: 'constructionWorkers', count: '976,980' },
  { key: 'carpenters',          count: '425,345' },
  { key: 'electricians',        count: '411,400' },
  { key: 'plumbers',            count: '243,790' },
  { key: 'welders',             count: '242,425' },
  { key: 'mechanics',           count: '207,065' },
  { key: 'hvacTechnicians',     count: '192,365' },
];

const WHO_WE_SERVE = [
  'welders', 'electricians', 'plumbers', 'machinists', 'maintenanceMechanics',
  'machineOperators', 'hvacTechnicians', 'carpenters', 'constructionWorkers',
  'pipefitters', 'millwrights', 'industrialWorkers', 'manufacturingWorkers',
];


export default function LandingPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [wlForm, setWlForm]         = useState({ name: '', email: '', userType: 'worker', trade: '', city: '', zip: '' });
  const [wlLoading, setWlLoading]   = useState(false);
  const [wlSubmitted, setWlSubmitted] = useState(false);
  const [showOutOfArea, setShowOutOfArea] = useState(false);

  const handleWaitlist = async (e) => {
    e.preventDefault();
    if (!wlForm.city.trim()) { alert(t('marketing.enterCityAlert')); return; }
    if (wlForm.userType === 'worker' && !wlForm.trade) { alert(t('marketing.selectTradeAlert')); return; }
    setWlLoading(true);
    try {
      const { data, error } = await supabase.from('waitlist').insert({
        full_name: wlForm.name,
        email:     wlForm.email,
        user_type: wlForm.userType,
        trade:     wlForm.userType === 'employer' ? null : (wlForm.trade || null),
        city:      wlForm.city  || null,
        zip:       wlForm.zip   || null,
      });
      console.log('[waitlist] insert result:', { data, error });
      if (error) console.log('[waitlist] error details:', JSON.stringify(error));
      fetch('/api/notify-interest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'waitlist_confirmation', recipient_email: wlForm.email, recipient_name: wlForm.name }),
      }).catch(() => {});
      setWlSubmitted(true);
    } catch (err) {
      console.error('Waitlist signup error:', err);
    } finally {
      setWlLoading(false);
    }
  };

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);

    const style = document.createElement('style');
    style.textContent = `
      *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
      html { scroll-behavior: smooth; }
      body { background-color: ${NAVY}; }
      .lp-nav-link:hover { text-decoration: underline; }
      @media (max-width: 768px) {
        .lp-nav-audience     { display: none !important; }
        .lp-nav-logo         { flex: 1 !important; }
        .lp-nav-signin       { font-size: 12px !important; }
        .lp-nav-right        { gap: 8px !important; }
        .lp-nav-waitlist-btn { padding: 8px 14px !important; font-size: 12px !important; white-space: nowrap !important; }
        .lp-hero-ctas  { flex-direction: column !important; align-items: stretch !important; }
        .lp-hero-ctas > * { width: 100% !important; justify-content: center; }
        .lp-stats-grid { grid-template-columns: 1fr 1fr !important; }
        .lp-stat-divider { display: none !important; }
        .lp-stat-label { font-size: 0.875rem !important; }
        .lp-shortage-grid { grid-template-columns: 1fr !important; }
        .lp-hiw-cols { flex-direction: column !important; }
        .lp-why-grid { grid-template-columns: 1fr !important; }
        .lp-team-grid { flex-direction: column !important; align-items: center !important; }
        .lp-team-card { width: 100% !important; max-width: 420px !important; }
        .lp-final-ctas { flex-direction: column !important; align-items: center !important; }
        .lp-final-ctas > * { width: 100% !important; max-width: 300px; }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(link);
      document.head.removeChild(style);
    };
  }, []);

  return (
    <div style={{ fontFamily: FONT_BODY, color: WHITE }}>

      {/* ── 1. NAVBAR ── */}
      <SiteNavbar
        activePage="home"
        primaryCta={{ label: t('marketing.joinWaitlist'), onClick: () => navigate('/worker/signup') }}
        secondaryCta={{ label: t('marketing.landing.browseResources'), variant: 'outline', onClick: () => navigate('/resources') }}
      />

      {/* ── 2. HERO ── */}
      <section className="lp-hero" style={{
        backgroundColor: NAVY, padding: '120px 2rem 80px',
        textAlign: 'center', position: 'relative', overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', top: '40%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '700px', height: '500px',
          background: 'radial-gradient(ellipse, rgba(245,166,35,0.11) 0%, transparent 65%)',
          pointerEvents: 'none',
        }} />

        <div style={{ position: 'relative', maxWidth: '900px', margin: '0 auto' }}>
          <h1 style={{
            fontFamily: FONT_HEAD,
            fontSize: 'clamp(4.5rem, 13vw, 9rem)',
            color: WHITE, letterSpacing: '0.03em',
            lineHeight: 0.95, marginBottom: '1.5rem',
          }}>
            {t('marketing.landing.heroTitlePre1')} <span style={{ color: AMBER }}>{t('marketing.landing.heroTitleAmber1')}</span><br />
            {t('marketing.landing.heroTitlePre2')} <span style={{ color: AMBER }}>{t('marketing.landing.heroTitleAmber2')}</span>
          </h1>

          <p style={{
            fontSize: 'clamp(1.1rem, 2.5vw, 1.35rem)',
            color: 'rgba(255,255,255,0.85)',
            marginBottom: '1.25rem', fontWeight: 500, lineHeight: 1.5,
          }}>
            {t('marketing.landing.heroSubtitle')}
          </p>

          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <button
              onClick={() => navigate('/worker/signup')}
              style={{
                backgroundColor: AMBER, color: NAVY, border: 'none',
                borderRadius: '8px', padding: '16px 40px',
                fontWeight: 700, fontSize: '1.05rem', cursor: 'pointer', fontFamily: FONT_BODY,
              }}
            >
              {t('marketing.joinWaitlist')}
            </button>
          </div>

          {/* Genuine no-pressure entry point for visitors (students especially) who aren't
              ready to sign up yet -- just a plain link, not styled as a competing CTA. */}
          <p style={{ marginTop: '1.25rem', fontSize: '0.9rem' }}>
            <span
              onClick={() => navigate('/resources')}
              style={{ color: 'rgba(255,255,255,0.6)', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: '3px' }}
            >
              {t('marketing.landing.notReadyLink')}
            </span>
          </p>
        </div>
      </section>

      {/* ── 3. STATS BAR ── */}
      <section style={{ backgroundColor: WHITE, padding: '3rem 2rem' }}>
        <div
          className="lp-stats-grid"
          style={{
            maxWidth: '1100px', margin: '0 auto',
            display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
          }}
        >
          {[
            { stat: '88,000+',                     label: t('marketing.stats.newUnionApprentices2025') },
            { stat: '2.1M+',                       label: t('marketing.stats.tradeJobsUnfilled') },
            // 41 trades in the Career Pay Guide (trade_career_data, Illinois), rounded down.
            { stat: t('marketing.landing.statPayTrades'), label: t('marketing.landing.statPayTradesLabel') },
            { stat: t('marketing.landing.statChicago'),  label: t('marketing.stats.firstMarket') },
          ].map((item, i) => (
            <Reveal stagger key={i} style={{ textAlign: 'center', padding: '0.75rem 1.5rem', position: 'relative' }}>
              {i < 3 && (
                <div className="lp-stat-divider" style={{
                  position: 'absolute', right: 0, top: '15%', bottom: '15%',
                  width: '2px', backgroundColor: AMBER,
                }} />
              )}
              <div style={{
                fontFamily: FONT_HEAD, fontSize: 'clamp(2rem, 4vw, 3rem)',
                color: AMBER, letterSpacing: '0.02em', lineHeight: 1.1,
              }}>
                <CountUp value={item.stat} />
              </div>
              <div className="lp-stat-label" style={{ fontSize: '0.8rem', color: '#666', fontWeight: 500, marginTop: '6px', lineHeight: 1.4 }}>
                {item.label}
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ── 4. LABOR SHORTAGE SECTION ── */}
      <section style={{ backgroundColor: WHITE, padding: '6rem 2rem', borderTop: '1px solid #eee' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <Reveal as="h2" style={{
            fontFamily: FONT_HEAD, fontSize: 'clamp(2.25rem, 5vw, 3.5rem)',
            color: NAVY, letterSpacing: '0.03em', textAlign: 'center', marginBottom: '1.25rem',
          }}>
            {t('marketing.landing.crisisTitle')}
          </Reveal>
          <Reveal as="p" style={{
            fontSize: '0.975rem', color: '#555', lineHeight: 1.8,
            maxWidth: '720px', margin: '0 auto 3rem', textAlign: 'center',
          }}>
            {t('marketing.landing.crisisBody')}
          </Reveal>

          <div
            className="lp-shortage-grid"
            style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginBottom: '2.5rem' }}
          >
            {SHORTAGE_TRADES.map(trade => (
              <Reveal stagger key={trade.key} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '1rem 1.5rem', backgroundColor: '#F8F8F8',
                borderRadius: '10px', border: '1px solid #eee',
              }}>
                <span style={{ fontWeight: 700, color: NAVY, fontSize: '0.95rem' }}>{t(`marketing.tradesPlural.${trade.key}`)}</span>
                <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: '12px' }}>
                  <div style={{ fontFamily: FONT_HEAD, fontSize: '1.5rem', color: AMBER, letterSpacing: '0.02em', lineHeight: 1 }}>
                    {trade.count}
                  </div>
                  <div style={{ fontSize: '0.7rem', color: '#888', marginTop: '2px' }}>{t('marketing.landing.projectedOpenings')}</div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal as="p" style={{ textAlign: 'center', fontSize: '1rem', color: NAVY, fontWeight: 600, lineHeight: 1.6 }}>
            {t('marketing.landing.crisisClosing')}
          </Reveal>
        </div>
      </section>

      {/* ── 5. HOW IT WORKS ── */}
      <section style={{ backgroundColor: GRAY_BG, padding: '6rem 2rem' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <Reveal as="h2" style={{
            fontFamily: FONT_HEAD, fontSize: 'clamp(2.5rem, 5vw, 3.75rem)',
            color: NAVY, letterSpacing: '0.03em', textAlign: 'center', marginBottom: '3.5rem',
          }}>
            {t('marketing.landing.howItWorksTitle')}
          </Reveal>

          <div className="lp-hiw-cols" style={{ display: 'flex', gap: '2rem' }}>
            {/* Workers */}
            <Reveal stagger style={{
              flex: 1, backgroundColor: WHITE, borderRadius: '16px',
              padding: '2.5rem', border: `2px solid ${AMBER}`,
            }}>
              <div style={{
                display: 'inline-block', backgroundColor: AMBER, color: NAVY,
                fontFamily: FONT_HEAD, fontSize: '0.9rem', letterSpacing: '0.1em',
                padding: '5px 14px', borderRadius: '4px', marginBottom: '1.75rem',
              }}>
                {t('marketing.landing.forWorkersBadge')}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', marginBottom: '2rem' }}>
                {[
                  { icon: 'search', title: t('marketing.landing.workerStep1Title'), desc: t('marketing.landing.workerStep1Desc') },
                  { icon: 'briefcase', title: t('marketing.landing.workerStep2Title'), desc: t('marketing.landing.workerStep2Desc') },
                  { icon: 'message', title: t('marketing.landing.workerStep3Title'), desc: t('marketing.landing.workerStep3Desc') },
                  { icon: 'checkCircle', title: t('marketing.landing.workerStep4Title'), desc: t('marketing.landing.workerStep4Desc') },
                ].map((step, i) => (
                  <div key={i} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                    <div style={{
                      width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                      backgroundColor: 'rgba(245,166,35,0.12)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <LineIcon name={step.icon} size={20}  color={AMBER} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, color: NAVY, fontSize: '0.95rem', marginBottom: '3px' }}>{step.title}</div>
                      <div style={{ fontSize: '0.85rem', color: '#555', lineHeight: 1.55 }}>{step.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
              <button
                onClick={() => navigate('/worker/signup')}
                style={{
                  width: '100%', backgroundColor: AMBER, color: NAVY,
                  border: 'none', borderRadius: '8px', padding: '14px',
                  fontWeight: 700, fontSize: '1rem', cursor: 'pointer', fontFamily: FONT_BODY,
                }}
              >
                {t('marketing.joinWaitlist')}
              </button>
            </Reveal>

            {/* Employers */}
            <Reveal stagger style={{
              flex: 1, backgroundColor: WHITE, borderRadius: '16px',
              padding: '2.5rem', border: `2px solid ${NAVY}`,
            }}>
              <div style={{
                display: 'inline-block', backgroundColor: NAVY, color: WHITE,
                fontFamily: FONT_HEAD, fontSize: '0.9rem', letterSpacing: '0.1em',
                padding: '5px 14px', borderRadius: '4px', marginBottom: '1.75rem',
              }}>
                {t('marketing.landing.forEmployersBadge')}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', marginBottom: '2rem' }}>
                {[
                  { icon: 'clipboard', title: t('marketing.landing.employerStep1Title'), desc: t('marketing.landing.employerStep1Desc') },
                  { icon: 'target', title: t('marketing.landing.employerStep2Title'), desc: t('marketing.landing.employerStep2Desc') },
                  { icon: 'message', title: t('marketing.landing.employerStep3Title'), desc: t('marketing.landing.employerStep3Desc') },
                  { icon: 'signature', title: t('marketing.landing.employerStep4Title'), desc: t('marketing.landing.employerStep4Desc') },
                ].map((step, i) => (
                  <div key={i} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                    <div style={{
                      width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                      backgroundColor: 'rgba(13,27,42,0.07)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <LineIcon name={step.icon} size={20}  color={AMBER} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, color: NAVY, fontSize: '0.95rem', marginBottom: '3px' }}>{step.title}</div>
                      <div style={{ fontSize: '0.85rem', color: '#555', lineHeight: 1.55 }}>{step.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
              {/* Employers pay (Basic/Standard/Plus), so this one can't claim a "free profile"
                  the way the worker column truthfully can. */}
              <button
                onClick={() => navigate('/employer/signup')}
                style={{
                  width: '100%', backgroundColor: 'transparent', color: NAVY,
                  border: `2px solid ${NAVY}`, borderRadius: '8px', padding: '14px',
                  fontWeight: 700, fontSize: '1rem', cursor: 'pointer', fontFamily: FONT_BODY,
                }}
              >
                {t('marketing.createEmployerAccount')}
              </button>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── 6. WHY VOCATION WORKS ── */}
      <section style={{ backgroundColor: NAVY, padding: '6rem 2rem' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <Reveal as="h2" style={{
            fontFamily: FONT_HEAD, fontSize: 'clamp(2.5rem, 5vw, 3.75rem)',
            color: AMBER, letterSpacing: '0.03em', textAlign: 'center', marginBottom: '3.5rem',
          }}>
            {t('marketing.landing.whyTitle')}
          </Reveal>

          <div
            className="lp-why-grid"
            style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem' }}
          >
            {[
              { icon: 'target', title: t('marketing.landing.why1Title'), desc: t('marketing.landing.why1Desc') },
              { icon: 'clipboard', title: t('marketing.landing.why2Title'), desc: t('marketing.landing.why2Desc') },
              { icon: 'city', title: t('marketing.landing.why3Title'), desc: t('marketing.landing.why3Desc') },
            ].map((item, i) => (
              <Reveal stagger key={i} style={{
                backgroundColor: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.07)',
                borderTop: `3px solid ${AMBER}`,
                borderRadius: '12px', padding: '2rem',
              }}>
                <div style={{ marginBottom: '1rem' }}><LineIcon name={item.icon} size={34}  color={AMBER} /></div>
                <h3 style={{
                  fontFamily: FONT_HEAD, fontSize: '1.4rem',
                  color: WHITE, letterSpacing: '0.03em', marginBottom: '0.75rem',
                }}>
                  {item.title.toUpperCase()}
                </h3>
                <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.75 }}>
                  {item.desc}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── 7. ABOUT US — OUR STORY ── */}
      <section style={{ backgroundColor: WHITE, padding: '6rem 2rem' }}>
        <Reveal style={{ maxWidth: '700px', margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontFamily: FONT_HEAD, fontSize: 'clamp(2.25rem, 5vw, 3.5rem)', color: NAVY, letterSpacing: '0.03em', marginBottom: '0.75rem' }}>
            {t('marketing.landing.storyTitle')}
          </h2>
          <div style={{ width: '48px', height: '3px', backgroundColor: AMBER, margin: '0 auto 2.5rem' }} />
          <p style={{ fontFamily: FONT_BODY, fontSize: '1rem', color: '#333333', lineHeight: 1.8, marginBottom: '1.5rem', textAlign: 'left' }}>
            {t('marketing.landing.storyP1')}
          </p>
          <p style={{ fontFamily: FONT_BODY, fontSize: '1rem', color: '#333333', lineHeight: 1.8, marginBottom: '1.5rem', textAlign: 'left' }}>
            {t('marketing.landing.storyP2')}
          </p>
          <p style={{ fontFamily: FONT_BODY, fontSize: '1rem', color: '#333333', lineHeight: 1.8, marginBottom: '1.5rem', textAlign: 'left' }}>
            {t('marketing.landing.storyP3')}
          </p>
          <p style={{ fontFamily: FONT_BODY, fontSize: '1rem', color: '#333333', lineHeight: 1.8, marginBottom: '1.5rem', textAlign: 'left' }}>
            {t('marketing.landing.storyP4')}
          </p>
          <p style={{ fontFamily: FONT_BODY, fontSize: '1rem', color: '#333333', lineHeight: 1.8, marginBottom: '2rem', textAlign: 'left' }}>
            {t('marketing.landing.storyP5')}
          </p>
          <p style={{ fontFamily: FONT_BODY, fontSize: '1rem', color: AMBER, fontStyle: 'italic', textAlign: 'center' }}>
            {t('marketing.landing.storySignature')}
          </p>
        </Reveal>
      </section>

      {/* ── 7B. ABOUT US — MEET THE TEAM ── */}
      <section style={{ backgroundColor: '#F7F7F7', padding: '6rem 2rem' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto', textAlign: 'center' }}>
          <Reveal as="h2" style={{ fontFamily: FONT_HEAD, fontSize: 'clamp(2.25rem, 5vw, 3.5rem)', color: NAVY, letterSpacing: '0.03em', marginBottom: '3rem' }}>
            {t('marketing.landing.teamTitle')}
          </Reveal>
          <div className="lp-team-grid" style={{ display: 'flex', gap: '2rem', justifyContent: 'center' }}>

            {/* Alexis Sanchez */}
            <Reveal stagger className="lp-team-card" style={{ flex: '0 1 420px', backgroundColor: WHITE, borderRadius: '8px', padding: '32px', boxShadow: '0 2px 16px rgba(13,27,42,0.08)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <img src="/alexis.jpg" alt="Alexis, Co-Founder & CEO" style={{ width: 140, height: 140, borderRadius: '50%', objectFit: 'cover', marginBottom: '1.25rem', flexShrink: 0 }} />
              <p style={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: '1.1rem', color: NAVY, marginBottom: '4px' }}>{t('marketing.landing.alexisName')}</p>
              <p style={{ fontFamily: FONT_BODY, fontSize: '0.88rem', color: AMBER, fontWeight: 600, marginBottom: '1rem' }}>{t('marketing.landing.alexisRole')}</p>
              <p style={{ fontFamily: FONT_BODY, fontSize: '0.9rem', color: '#555555', lineHeight: 1.75, textAlign: 'center' }}>
                {t('marketing.landing.alexisBio')}
              </p>
            </Reveal>

            {/* Eric Sarpong */}
            <Reveal stagger className="lp-team-card" style={{ flex: '0 1 420px', backgroundColor: WHITE, borderRadius: '8px', padding: '32px', boxShadow: '0 2px 16px rgba(13,27,42,0.08)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <img src="/eric.jpg" alt="Eric Sarpong, Co-Founder & COO" style={{ width: 140, height: 140, borderRadius: '50%', objectFit: 'cover', marginBottom: '1.25rem', flexShrink: 0 }} />
              <p style={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: '1.1rem', color: NAVY, marginBottom: '4px' }}>{t('marketing.landing.ericName')}</p>
              <p style={{ fontFamily: FONT_BODY, fontSize: '0.88rem', color: AMBER, fontWeight: 600, marginBottom: '1rem' }}>{t('marketing.landing.ericRole')}</p>
              <p style={{ fontFamily: FONT_BODY, fontSize: '0.9rem', color: '#555555', lineHeight: 1.75, textAlign: 'center' }}>
                {t('marketing.landing.ericBio')}
              </p>
            </Reveal>

          </div>
        </div>
      </section>

      {/* ── 8. WHO WE SERVE ── */}
      <section style={{ backgroundColor: WHITE, padding: '6rem 2rem' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto', textAlign: 'center' }}>
          <Reveal as="h2" style={{
            fontFamily: FONT_HEAD, fontSize: 'clamp(2.25rem, 5vw, 3.5rem)',
            color: NAVY, letterSpacing: '0.03em', marginBottom: '2.5rem',
          }}>
            {t('marketing.landing.whoWeServeTitle')}
          </Reveal>

          <Reveal style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', justifyContent: 'center', marginBottom: '2rem' }}>
            {WHO_WE_SERVE.map((tradeKey, i) => (
              <span key={i} style={{
                padding: '9px 20px', borderRadius: '999px',
                backgroundColor: i % 2 === 0 ? AMBER : NAVY,
                color: i % 2 === 0 ? NAVY : WHITE,
                fontWeight: 600, fontSize: '0.875rem',
              }}>
                {t(`marketing.tradesPlural.${tradeKey}`)}
              </span>
            ))}
          </Reveal>

          <Reveal as="p" style={{ fontSize: '0.925rem', color: '#666', lineHeight: 1.6 }}>
            {t('marketing.landing.whoWeServeFooter')}
          </Reveal>
        </div>
      </section>

      {/* ── 8. GET STARTED + OUT-OF-AREA FORM ── */}
      <section id="cta" style={{ backgroundColor: AMBER, padding: '5rem 2rem' }}>
        <div style={{ maxWidth: '540px', margin: '0 auto' }}>
          <Reveal as="h2" style={{
            fontFamily: FONT_HEAD, fontSize: 'clamp(2.75rem, 7vw, 4.5rem)',
            color: NAVY, letterSpacing: '0.03em', textAlign: 'center', marginBottom: '1rem',
          }}>
            {t('marketing.landing.ctaTitle')}
          </Reveal>
          <Reveal as="p" style={{
            fontSize: '1rem', color: 'rgba(13,27,42,0.72)', textAlign: 'center',
            marginBottom: '2rem', fontWeight: 500, lineHeight: 1.65,
          }}>
            {t('marketing.landing.ctaSubtitle')}
          </Reveal>

          {/* The product is live, so signing up is the primary action here. The waitlist form
              below is now only for people outside the one market we actually serve. */}
          {!wlSubmitted && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.25rem', marginBottom: showOutOfArea ? '2.5rem' : 0 }}>
              <button
                data-cta-primary
                onClick={() => navigate('/worker/signup')}
                style={{
                  backgroundColor: NAVY, color: WHITE, border: 'none',
                  borderRadius: '8px', padding: '16px 40px', maxWidth: '100%',
                  fontWeight: 700, fontSize: '1.05rem', cursor: 'pointer', fontFamily: FONT_BODY,
                }}
              >
                {t('marketing.joinWaitlist')}
              </button>
              {!showOutOfArea && (
                <button
                  data-out-of-area-toggle
                  onClick={() => setShowOutOfArea(true)}
                  style={{
                    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                    fontFamily: FONT_BODY, fontSize: '0.9rem', fontWeight: 600,
                    color: 'rgba(13,27,42,0.72)', textDecoration: 'underline',
                    textUnderlineOffset: '3px', maxWidth: '100%',
                  }}
                >
                  {t('marketing.outOfAreaLink')}
                </button>
              )}
            </div>
          )}

          {showOutOfArea && !wlSubmitted && (
            <>
              <h3 style={{
                fontFamily: FONT_HEAD, fontSize: 'clamp(1.75rem, 4.5vw, 2.5rem)',
                color: NAVY, letterSpacing: '0.03em', textAlign: 'center', marginBottom: '0.75rem',
              }}>
                {t('marketing.landing.outOfAreaTitle')}
              </h3>
              <p style={{
                fontSize: '0.95rem', color: 'rgba(13,27,42,0.72)', textAlign: 'center',
                marginBottom: '2rem', fontWeight: 500, lineHeight: 1.65,
              }}>
                {t('marketing.landing.outOfAreaSubtitle')}
              </p>
            </>
          )}

          {wlSubmitted ? (
            <div style={{
              backgroundColor: NAVY, borderRadius: '12px', padding: '2.5rem',
              textAlign: 'center',
            }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}><LineIcon name="checkCircle" size={44}  color={AMBER} /></div>
              <h3 style={{
                fontFamily: FONT_HEAD, fontSize: '1.75rem', color: AMBER,
                letterSpacing: '0.05em', marginBottom: '0.75rem',
              }}>
                {t('marketing.landing.confirmedTitle')}
              </h3>
              <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.95rem', lineHeight: 1.7 }}>
                {t('marketing.landing.confirmedBody')}<br />{t('marketing.landing.confirmedSignature')}
              </p>
            </div>
          ) : !showOutOfArea ? null : (
            <form
              onSubmit={handleWaitlist}
              style={{
                backgroundColor: WHITE, borderRadius: '12px', padding: '2rem',
                display: 'flex', flexDirection: 'column', gap: '14px',
              }}
            >
              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: NAVY, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '6px' }}>
                  {t('marketing.landing.fullNameLabel')}
                </label>
                <input
                  type="text" required value={wlForm.name}
                  onChange={e => setWlForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Jane Smith"
                  style={{ width: '100%', padding: '11px 13px', border: '1.5px solid rgba(13,27,42,0.18)', borderRadius: '7px', fontSize: '0.95rem', fontFamily: FONT_BODY, color: NAVY, backgroundColor: WHITE, outline: 'none' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: NAVY, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '6px' }}>
                  {t('marketing.landing.emailLabel')}
                </label>
                <input
                  type="email" required value={wlForm.email}
                  onChange={e => setWlForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="jane@example.com"
                  style={{ width: '100%', padding: '11px 13px', border: '1.5px solid rgba(13,27,42,0.18)', borderRadius: '7px', fontSize: '0.95rem', fontFamily: FONT_BODY, color: NAVY, backgroundColor: WHITE, outline: 'none' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: NAVY, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '6px' }}>
                  {t('marketing.landing.iAmALabel')}
                </label>
                <select
                  required value={wlForm.userType}
                  onChange={e => setWlForm(f => ({ ...f, userType: e.target.value }))}
                  style={{ width: '100%', padding: '11px 13px', border: '1.5px solid rgba(13,27,42,0.18)', borderRadius: '7px', fontSize: '0.95rem', fontFamily: FONT_BODY, color: NAVY, backgroundColor: WHITE, outline: 'none', appearance: 'auto' }}
                >
                  <option value="worker">{t('marketing.landing.workerOption')}</option>
                  <option value="employer">{t('marketing.landing.employerOption')}</option>
                </select>
              </div>
              {wlForm.userType !== 'employer' && (
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: NAVY, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '6px' }}>
                    {t('marketing.landing.whatIsYourTradeLabel')}{wlForm.userType === 'worker' ? ' *' : ''}
                  </label>
                  <select
                    required={wlForm.userType === 'worker'}
                    value={wlForm.trade}
                    onChange={e => setWlForm(f => ({ ...f, trade: e.target.value }))}
                    style={{ width: '100%', padding: '11px 13px', border: '1.5px solid rgba(13,27,42,0.18)', borderRadius: '7px', fontSize: '0.95rem', fontFamily: FONT_BODY, color: NAVY, backgroundColor: WHITE, outline: 'none', appearance: 'auto' }}
                  >
                    <option value="">{t('marketing.tradeOptions.selectYourTrade')}</option>
                    <option value="Electrician">{t('marketing.tradeOptions.electrician')}</option>
                    <option value="HVAC Technician">{t('marketing.tradeOptions.hvacTechnician')}</option>
                    <option value="Welder">{t('marketing.tradeOptions.welder')}</option>
                    <option value="Plumber">{t('marketing.tradeOptions.plumber')}</option>
                    <option value="Pipefitter">{t('marketing.tradeOptions.pipefitter')}</option>
                    <option value="Machinist / CNC Operator">{t('marketing.tradeOptions.machinistCncOperator')}</option>
                    <option value="Carpenter">{t('marketing.tradeOptions.carpenter')}</option>
                    <option value="Automotive Tech">{t('marketing.tradeOptions.automotiveTech')}</option>
                    <option value="Diesel Mechanic">{t('marketing.tradeOptions.dieselMechanic')}</option>
                    <option value="Industrial Maintenance">{t('marketing.tradeOptions.industrialMaintenance')}</option>
                    <option value="Machine Operator">{t('marketing.tradeOptions.machineOperator')}</option>
                    <option value="General Maintenance">{t('marketing.tradeOptions.generalMaintenance')}</option>
                    <option value="Looking to choose a trade">{t('marketing.tradeOptions.lookingToChoose')}</option>
                    <option value="Other">{t('marketing.tradeOptions.other')}</option>
                  </select>
                </div>
              )}
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: NAVY, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '6px' }}>
                    {t('marketing.landing.cityLabel')}
                  </label>
                  <input
                    type="text" required value={wlForm.city}
                    onChange={e => setWlForm(f => ({ ...f, city: e.target.value }))}
                    placeholder="Milwaukee"
                    style={{ width: '100%', padding: '11px 13px', border: '1.5px solid rgba(13,27,42,0.18)', borderRadius: '7px', fontSize: '0.95rem', fontFamily: FONT_BODY, color: NAVY, backgroundColor: WHITE, outline: 'none' }}
                  />
                </div>
                <div style={{ width: '130px' }}>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: NAVY, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '6px' }}>
                    {t('marketing.landing.zipLabel')}
                  </label>
                  <input
                    type="text" value={wlForm.zip}
                    onChange={e => setWlForm(f => ({ ...f, zip: e.target.value }))}
                    placeholder="53202"
                    style={{ width: '100%', padding: '11px 13px', border: '1.5px solid rgba(13,27,42,0.18)', borderRadius: '7px', fontSize: '0.95rem', fontFamily: FONT_BODY, color: NAVY, backgroundColor: WHITE, outline: 'none' }}
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={wlLoading}
                style={{
                  width: '100%', backgroundColor: NAVY, color: WHITE,
                  border: 'none', borderRadius: '8px', padding: '14px',
                  fontWeight: 700, fontSize: '1rem', cursor: wlLoading ? 'not-allowed' : 'pointer',
                  fontFamily: FONT_BODY, marginTop: '4px',
                  opacity: wlLoading ? 0.7 : 1,
                }}
              >
                {wlLoading ? t('marketing.landing.joining') : t('marketing.landing.joinTheWaitlist')}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* ── 9. FOOTER ── */}
      <SiteFooter />

    </div>
  );
}
